<template>
  <div class="sms-text-box-editor-wrapper">
    <label class="floating-label">{{ label }}</label>
    <div
      ref="editor"
      contenteditable="true"
      class="sms-text-box-editor"
      :class="error ? 'danger' : ''"
      @input="updateModel"
      @keydown="handleKeydown"
      @blur="handleBlur"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'SMSTextBoxEditor',
  props: {
    value: String,
    error: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    interpolations: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(interpolations) {
        return interpolations.every((interpolation) => typeof interpolation === 'object'
          && interpolation !== null
          && 'shorthand' in interpolation
          && typeof interpolation.shorthand === 'string'
          && 'attribute' in interpolation
          && typeof interpolation.attribute === 'string');
      },
    },
  },
  data() {
    return {

    };
  },
  computed: {
    valueHtml() {
      return this.interpolations.reduce((html, interpolation) => {
        const regex = new RegExp(interpolation.shorthand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'); // Escape regex

        return html.replace(
          regex,
          `<span class="placeholder" contenteditable="false">
            ${interpolation.name}
            <span class="remove-placeholder">✖</span>
          </span>`,
        );
      }, this.value || '');
    },
  },
  watch: {
    value(newVal) {
      if (newVal !== this.extractPlainText()) {
        const hasFocus = document.activeElement === this.$refs.editor;

        this.setCaretPosition(() => {
          if (this.valueHtml.endsWith(' ')) {
            this.$refs.editor.innerHTML = `${this.valueHtml.slice(0, -1)}&nbsp;`;
          } else {
            this.$refs.editor.innerHTML = this.valueHtml;
          }
        });

        this.addRemoveListeners();

        if (!hasFocus) {
          this.$refs.editor.focus();
          this.moveCaretToEnd();
        }
      }
    },
  },
  mounted() {
    if (this.value) {
      this.$refs.editor.innerHTML = this.valueHtml;
      this.addRemoveListeners();
    }
  },
  methods: {
    moveCaretToEnd() {
      const selection = window.getSelection();
      const range = document.createRange();
      const { editor } = this.$refs;

      range.selectNodeContents(editor);
      range.collapse(false);

      selection.removeAllRanges();
      selection.addRange(range);
    },
    setCaretPosition(callback) {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const { startOffset } = range;
      const startNode = range.startContainer;

      callback();

      const newRange = document.createRange();
      newRange.setStart(startNode, Math.min(startOffset, startNode.length));
      newRange.setEnd(startNode, Math.min(startOffset, startNode.length));

      selection.removeAllRanges();
      selection.addRange(newRange);
    },
    extractPlainText() {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.$refs.editor.innerHTML;

      this.interpolations.forEach((interpolation) => {
        const spans = tempDiv.querySelectorAll('.placeholder');

        spans.forEach((span) => {
          const spanPlaceholder = span.textContent.replace('✖', '').trim();

          if (spanPlaceholder === interpolation.name) {
            span.replaceWith(` ${interpolation.shorthand}`);
          }
        });
      });

      let text = tempDiv.textContent;

      text = text.replace(/\u00A0/g, ' '); // `&nbsp;` → normal space
      text = text.replace(/\s+/g, ' ').trim();

      return text;
    },
    updateModel() {
      this.$emit('input', this.extractPlainText());
    },
    handleKeydown(event) {
      if (event.target.closest('.placeholder')) {
        event.preventDefault();
      }
    },
    handleBlur() {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      const marker = document.createTextNode('|');
      range.insertNode(marker);

      const plainTextWithMarker = this.extractPlainText();
      const plainCursorPosition = plainTextWithMarker.indexOf('|');

      marker.parentNode.removeChild(marker);

      this.cursorPosition = plainCursorPosition;
      this.$emit('blur', this.cursorPosition);
    },
    addRemoveListeners() {
      this.$nextTick(() => {
        const removeIcons = this.$refs.editor.querySelectorAll('.remove-placeholder');
        removeIcons.forEach((icon) => {
          icon.addEventListener('click', (event) => {
            event.stopPropagation();
            const placeholder = event.target.closest('.placeholder');
            if (placeholder) {
              placeholder.remove();
            }

            this.updateModel();
          });
        });
      });
    },
  },
};
</script>

<style lang="scss">
.sms-text-box-editor-wrapper {
  position: relative;
  width: 100%;

  .floating-label {
    position: absolute;
    top: -10px;
    left: 10px;
    background: white;
    padding: 0 5px;
    font-size: 12px;
    color: gray;
    pointer-events: none;
  }

  .sms-text-box-editor {
    min-height: 100px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    padding: 10px;
    outline: none;
    border-radius: 6px;
    line-height: 1.6;

    &.danger {
      border-color: rgba(var(--vs-danger), 1);
    }

    .placeholder {
      background-color: rgba(var(--vs-primary), 0.1);
      padding: 2px 5px;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      line-height: 1;
    }

    .remove-placeholder {
      cursor: pointer;
      font-size: 14px;
      color: rgba(var(--vs-primary), 1);
    }
  }

}
</style>
